package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initS3knPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("s3kn.md", FrontMatterElement.Builder { addScalar("title", "s3kn"); addScalar("searchTitle", "S3 client for Kotlin/Native — SigV4, presigned URLs, no AWS SDK"); addScalar("description", "S3 on Linux, macOS, iOS and the JVM. SigV4 checked against all 34 official AWS vectors, presigned URLs on targets with no HTTP engine at all."); addScalar("library", "s3kn"); addScalar("post", "/blog/the-path-you-signed"); }))
}

@Page("/s3kn")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun S3knPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The AWS SDK for Kotlin publishes ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("JVM artefacts only")
        }
        org.jetbrains.compose.web.dom.Text(". On Kotlin/Native the two remaining options are binding ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aws-c-s3")
        }
        org.jetbrains.compose.web.dom.Text(" through cinterop — five C libraries and their build, on every target — or writing the protocol out. This is the second one: a signature and seven HTTP requests.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Signature Version 4")
            }
            org.jetbrains.compose.web.dom.Text(", header-based and in the query string, verified against all ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("34 official AWS test vectors")
            }
            org.jetbrains.compose.web.dom.Text(", including the four other SDKs skip.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("S3's own signing rule")
            }
            org.jetbrains.compose.web.dom.Text(", where the path is signed verbatim — no dot segments removed, no second round of encoding. Every other AWS service does the opposite.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Presigned URLs")
            }
            org.jetbrains.compose.web.dom.Text(" for reading and writing, to the seven-day ceiling. Presigning sends nothing, so it works on every KMP target, including those with no HTTP engine at all.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("put")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("get")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("delete")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("head")
            }
            org.jetbrains.compose.web.dom.Text(", streamed both ways, so a five-gigabyte object is never held whole.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Listing as a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Flow")
            }
            org.jetbrains.compose.web.dom.Text(" of pages with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("encoding-type=url")
            }
            org.jetbrains.compose.web.dom.Text(" always on, because an object key may hold characters XML 1.0 cannot represent.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Multipart upload, bounded memory, and an abort on ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("every")
            }
            org.jetbrains.compose.web.dom.Text(" path that can fail — parts nobody completes are billed until somebody notices.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories {
    mavenCentral()
    maven("https://reposilite.kotlin.website/snapshots")
}

dependencies {
    implementation("io.github.youndie:s3-client:0.1.0-SNAPSHOT")
    // The engine is yours, and which one exists depends on the target.
    implementation("io.ktor:ktor-client-curl:3.5.2")   // Linux, Windows, macOS
    implementation("io.ktor:ktor-client-darwin:3.5.2") // macOS, and the only choice on iOS
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("s3-sigv4")
        }
        org.jetbrains.compose.web.dom.Text(" can be taken on its own if all you need is signing or presigning — it has no HTTP dependency at all.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("platform-support-stated-honestly") }) {
        org.jetbrains.compose.web.dom.Text("Platform support, stated honestly")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(" is what this is built for and the only target it is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("claimed")
        }
        org.jetbrains.compose.web.dom.Text(" to work on: the whole suite runs there, live tests included. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jvm")
        }
        org.jetbrains.compose.web.dom.Text(" runs the same code and the same live tests in CI. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("macosArm64")
        }
        org.jetbrains.compose.web.dom.Text(" has had the full suite run against a real server by hand. iOS compiles and publishes; a device test has never been run.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("\"It compiles\" and \"it moved bytes to a real bucket\" are different statements, and only one of them belongs in a support table.")
    }
}
